import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, FormError } from '@lawbee/ui';
import { CircleCheck } from 'lucide-react';
import { useState } from 'react';
import { IntakeRenderer, intakeFormData, type IntakeSubmitPayload } from '../../components/IntakeRenderer';
import { api, ApiError, errorMessage, fieldErrors } from '../../lib/api';
import { useParam } from '../../lib/hooks';
import type { Answers, IntakeField } from '../../lib/types';
import { PublicFrame } from '../../shell/RootPages';
import { ConfidentialityNotice, DoneBlock, InvalidLink, publicQueryState, useEmbed } from './shared';

type Missing = { field: string; label: string }[];
type SubmitResponse = { ok: boolean; missing: Missing };

type IntakeFormResponse = {
  firm: { name: string; slug?: string; confidentialityNotice: string };
  form: { name: string; intro: string | null; fields: IntakeField[] };
  coreFields: IntakeField[];
};

type FollowupResponse = {
  firm: { name: string; confidentialityNotice?: string };
  firstName: string;
  fields: IntakeField[];
  answers: Answers;
};

const NO_RELATIONSHIP = 'This form does not create a lawyer-client relationship.';

/** Shared submit flow for intake and follow-up: server field errors, root error, result. */
function useIntakeSubmit(path: string) {
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [rootError, setRootError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (payload: IntakeSubmitPayload) => api.upload<SubmitResponse>(path, intakeFormData(payload)),
    onMutate: () => {
      setServerErrors({});
      setRootError(null);
    },
    onSuccess: () => window.scrollTo({ top: 0 }),
    onError: (err) => {
      const f = err instanceof ApiError && err.status === 400 ? fieldErrors(err) : {};
      setServerErrors(f);
      setRootError(Object.keys(f).length > 0 ? 'Please check the highlighted fields and submit again.' : errorMessage(err, 'Your information could not be sent. Please try again.'));
    },
  });
  return { mutation, serverErrors, rootError };
}

function MissingList({ missing }: { missing: Missing }) {
  if (missing.length === 0) return null;
  return (
    <Alert variant="info" title="The firm may ask you for these details later" className="text-left">
      <ul className="mt-1 list-disc pl-5">
        {missing.map((m) => (
          <li key={m.field}>{m.label}</li>
        ))}
      </ul>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Intake form
// ---------------------------------------------------------------------------

export function IntakePage() {
  const slug = useParam('slug');
  const formSlug = useParam('formSlug');
  const embed = useEmbed();
  const q = useQuery({
    queryKey: ['public', 'intake', slug, formSlug],
    queryFn: () => api.get<IntakeFormResponse>(`/api/public/intake/${encodeURIComponent(slug)}/${encodeURIComponent(formSlug)}`),
    retry: false,
  });
  const { mutation, serverErrors, rootError } = useIntakeSubmit(`/api/public/intake/${encodeURIComponent(slug)}/${encodeURIComponent(formSlug)}`);

  const state = publicQueryState(q, {
    embed,
    loadingLabel: 'Loading form',
    notFound: <InvalidLink title="This form is not available" description="The link may be out of date. Please contact the firm directly." />,
  });
  if (state) return state;
  const { firm, form } = q.data!;

  if (mutation.data) {
    return (
      <PublicFrame firmName={firm.name} embed={embed}>
        <DoneBlock icon={<CircleCheck />} title="Thank you">
          <p>{firm.name} will review your information and contact you.</p>
          <MissingList missing={mutation.data.missing} />
        </DoneBlock>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame firmName={firm.name} embed={embed}>
      <div className="space-y-6">
        <header className="space-y-2">
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.025em]">{form.name}</h1>
          {form.intro ? <p className="whitespace-pre-line text-[15px] leading-relaxed text-muted-foreground">{form.intro}</p> : null}
        </header>
        <ConfidentialityNotice notice={firm.confidentialityNotice}>
          <p className="font-medium text-foreground/80">{NO_RELATIONSHIP}</p>
        </ConfidentialityNotice>
        <div className="rounded-2xl bg-card p-5 shadow-card sm:p-7">
          <IntakeRenderer fields={form.fields} submitting={mutation.isPending} serverErrors={serverErrors} onSubmit={(payload) => mutation.mutate(payload)} />
          {rootError ? (
            <div className="mt-4">
              <FormError message={rootError} />
            </div>
          ) : null}
        </div>
      </div>
    </PublicFrame>
  );
}

// ---------------------------------------------------------------------------
// Follow-up (missing details)
// ---------------------------------------------------------------------------

export function FollowupPage() {
  const token = useParam('token');
  const q = useQuery({
    queryKey: ['public', 'followup', token],
    queryFn: () => api.get<FollowupResponse>(`/api/public/followup/${encodeURIComponent(token)}`),
    retry: false,
  });
  const { mutation, serverErrors, rootError } = useIntakeSubmit(`/api/public/followup/${encodeURIComponent(token)}`);

  const state = publicQueryState(q, {
    loadingLabel: 'Loading',
    notFound: <InvalidLink title="This link has expired" description="Follow-up links expire for your privacy. Please contact the firm and they can send you a new one." />,
  });
  if (state) return state;
  const data = q.data!;

  if (mutation.data) {
    return (
      <PublicFrame firmName={data.firm.name}>
        <DoneBlock icon={<CircleCheck />} title="Thank you">
          <p>{data.firm.name} has your updated information and will be in touch.</p>
          <MissingList missing={mutation.data.missing} />
        </DoneBlock>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame firmName={data.firm.name}>
      <div className="space-y-6">
        <header className="space-y-2">
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.025em]">{data.firstName ? `Hello ${data.firstName},` : 'Hello,'}</h1>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            {data.firm.name} needs a few more details to review your matter. Please fill in what you can below.
          </p>
        </header>
        {data.fields.length === 0 ? (
          <Alert variant="success" title="Nothing more is needed">
            The firm already has everything it asked for. You can close this page.
          </Alert>
        ) : (
          <div className="rounded-2xl bg-card p-5 shadow-card sm:p-7">
            <IntakeRenderer
              fields={data.fields}
              includeCore={false}
              mode="followup"
              initialAnswers={data.answers}
              submitLabel="Send details"
              submitting={mutation.isPending}
              serverErrors={serverErrors}
              onSubmit={(payload) => mutation.mutate(payload)}
            />
            {rootError ? (
              <div className="mt-4">
                <FormError message={rootError} />
              </div>
            ) : null}
          </div>
        )}
        <ConfidentialityNotice notice={data.firm.confidentialityNotice ?? null}>
          <p>{NO_RELATIONSHIP}</p>
        </ConfidentialityNotice>
      </div>
    </PublicFrame>
  );
}
